import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Brain, 
  Shield, 
  Moon, 
  Heart, 
  Activity, 
  Weight, 
  Cigarette, 
  RefreshCw,
  ArrowRight,
  Phone,
  Calendar
} from 'lucide-react';
import ServiceCard from '../components/ServiceCard';
import CTA from '../components/CTA';

const Services: React.FC = () => {
  const services = [
    {
      title: "Radiotherapy Anxiety Support",
      description: "Specialist hypnotherapy support to reduce fear and anxiety during radiotherapy treatment. Particularly supportive for head and neck cancer patients.",
      bullets: [
        "Stay calm and in control during treatment",
        "Reduce panic and claustrophobia with treatment mask",
        "Build resilience throughout your treatment journey",
        "Manage loss of appetite - a common side effect of radiotherapy"
      ],
      icon: <Heart className="w-6 h-6" alt="Heart icon representing care and support during treatment" />,
      specialContent: (
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-pink-900 mb-2">Specialized Cancer Support for Head & Neck Patients</h4>
          <p className="text-pink-800 text-sm mb-3">
            I understand the unique challenges of cancer treatment, particularly for head and neck cancer patients. 
            My approach focuses on helping you feel calm and in control during radiotherapy sessions, managing 
            anxiety about treatment masks, supporting appetite challenges, and emotional well-being throughout your journey.
          </p>
          <p className="text-pink-800 text-sm font-medium mb-2">
            3-Session Radiotherapy Program: Ideally completed between treatment planning and the start of radiotherapy for optimal results.
          </p>
          <div className="space-y-2">
            <a 
              href="/radiotherapy-anxiety" 
              className="inline-flex items-center text-pink-700 hover:text-pink-800 font-medium text-sm"
            >
              Learn More About Radiotherapy Support
              <ArrowRight className="w-4 h-4 ml-1" />
            </a>
            <br />
            <a 
              href="/audios" 
              className="inline-flex items-center text-pink-700 hover:text-pink-800 font-medium text-sm"
            >
              Radiotherapy Anxiety Audio Available
              <ArrowRight className="w-4 h-4 ml-1" />
            </a>
          </div>
        </div>
      )
    },
    {
      title: "Pain Management & Chronic Conditions",
      description: "Learn to manage chronic pain through hypnosis techniques that help reduce pain perception and improve quality of life with ongoing conditions.",
      bullets: [
        "Reduce pain perception and intensity",
        "Improve mobility and daily functioning",
        "Develop coping strategies for chronic conditions",
        "Enhance overall quality of life"
      ],
      icon: <Activity className="w-6 h-6" alt="Activity icon representing movement and pain management" />,
      specialContent: (
        <div className="mt-4 space-y-2">
          <a href="/ibs-hypnotherapy" className="inline-flex items-center text-accent-700 hover:text-accent-800 font-medium text-sm">
            IBS Hypnotherapy
            <ArrowRight className="w-4 h-4 ml-1" />
          </a>
          <br />
          <a href="/bladder-urgency-hypnotherapy" className="inline-flex items-center text-accent-700 hover:text-accent-800 font-medium text-sm">
            Bladder Urgency Hypnotherapy
            <ArrowRight className="w-4 h-4 ml-1" />
          </a>
          <br />
          <a href="/jaw-tension-hypnotherapy" className="inline-flex items-center text-accent-700 hover:text-accent-800 font-medium text-sm">
            Jaw Tension (TMJ/Bruxism)
            <ArrowRight className="w-4 h-4 ml-1" />
          </a>
        </div>
      )
    },
    {
      title: "Anxiety, Stress & Burnout",
      description: "Comprehensive support for managing anxiety, reducing stress levels, and recovering from burnout. Learn powerful techniques to regain control and find peace in your daily life.",
      bullets: [
        "Reduce anxiety symptoms and panic attacks",
        "Develop effective stress management strategies",
        "Restore work-life balance and energy",
        "Build resilience and emotional regulation"
      ],
      icon: <Brain className="w-6 h-6" alt="Brain icon representing anxiety and stress management" />,
      specialContent: (
        <div className="mt-4 space-y-2">
          <a href="/anxiety-stress" className="inline-flex items-center text-accent-700 hover:text-accent-800 font-medium text-sm">
            Learn More
            <ArrowRight className="w-4 h-4 ml-1" />
          </a>
          <br />
          <a href="/burnout-recovery" className="inline-flex items-center text-accent-700 hover:text-accent-800 font-medium text-sm">
            Burnout Recovery
            <ArrowRight className="w-4 h-4 ml-1" />
          </a>
        </div>
      )
    },
    {
      title: "Medical Procedure Anxiety",
      description: "Specialized support for anxiety related to medical procedures, surgery fears, and healthcare appointments. Feel calm and confident during medical treatments.",
      bullets: [
        "Overcome surgery fears and pre-procedure anxiety",
        "Manage healthcare appointment anxiety",
        "Reduce medical procedure phobias",
        "Build confidence for upcoming treatments"
      ],
      icon: <Shield className="w-6 h-6" alt="Shield icon representing protection and medical support" />,
      specialContent: (
        <div className="mt-4">
          <a href="/medical-procedure-anxiety" className="inline-flex items-center text-accent-700 hover:text-accent-800 font-medium text-sm">
            Learn More
            <ArrowRight className="w-4 h-4 ml-1" />
          </a>
        </div>
      )
    },
    {
      title: "Sleep Issues & Insomnia",
      description: "Address the root causes of sleep problems through hypnotherapy techniques that promote deep, restorative sleep and healthy sleep patterns.",
      bullets: [
        "Fall asleep faster and stay asleep longer",
        "Reduce racing thoughts at bedtime",
        "Establish healthy sleep routines",
        "Address underlying anxiety affecting sleep"
      ],
      icon: <Moon className="w-6 h-6" alt="Moon icon representing sleep and rest" />
    },
    {
      title: "Fears & Phobias",
      description: "Overcome specific fears and phobias that limit your life. From common phobias to specific anxieties, learn to face your fears with confidence.",
      bullets: [
        "Overcome specific phobias and fears",
        "Build confidence in previously avoided situations",
        "Develop coping strategies for anxiety triggers",
        "Regain control over fear responses"
      ],
      icon: <Shield className="w-6 h-6" alt="Shield icon representing protection from fears" />
    },
    {
      title: "Pain Management & Chronic Conditions",
      description: "Learn to manage chronic pain through hypnosis techniques that help reduce pain perception and improve quality of life with ongoing conditions.",
      bullets: [
        "Reduce pain perception and intensity",
        "Improve mobility and daily functioning",
        "Develop coping strategies for chronic conditions",
        "Enhance overall quality of life"
      ],
      icon: <Activity className="w-6 h-6" alt="Activity icon representing movement and pain management" />
    },
    {
      title: "Weight Management & Emotional Eating",
      description: "Address the emotional and psychological factors behind weight struggles, developing a healthy relationship with food and sustainable lifestyle changes.",
      bullets: [
        "Break emotional eating patterns",
        "Develop healthy relationship with food",
        "Increase motivation for exercise and healthy habits",
        "Build lasting lifestyle changes"
      ],
      icon: <Weight className="w-6 h-6" alt="Weight icon representing weight management" />,
      specialContent: (
        <div className="mt-4">
          <a href="/weight-mindset" className="inline-flex items-center text-accent-700 hover:text-accent-800 font-medium text-sm">
            Join Waitlist
            <ArrowRight className="w-4 h-4 ml-1" />
          </a>
        </div>
      )
    },
    {
      title: "OCD & Repetitive Thought Patterns",
      description: "Specialized support for managing obsessive thoughts and compulsive behaviors, helping you regain control over your mind and daily activities.",
      bullets: [
        "Reduce obsessive thoughts and worries",
        "Break compulsive behavior patterns",
        "Develop healthier coping mechanisms",
        "Regain control over daily activities"
      ],
      icon: <RefreshCw className="w-6 h-6" alt="Refresh icon representing repetitive patterns and OCD" />
    },
    {
      title: "Smoking & Vaping Cessation",
      description: "Comprehensive program to break free from nicotine addiction permanently, addressing both the physical and psychological aspects of smoking and vaping.",
      bullets: [
        "Quit smoking or vaping permanently",
        "Reduce cravings and withdrawal symptoms",
        "Boost confidence in your ability to quit",
        "Develop healthy coping strategies"
      ],
      icon: <Cigarette className="w-6 h-6" alt="Cigarette icon representing smoking cessation" />
    }
  ];

  const processSteps = [
    {
      step: "1",
      title: "10-Minute Call",
      description: "We'll discuss your goals, concerns, and how hypnotherapy can help you achieve the changes you want to make."
    },
    {
      step: "2", 
      title: "Personalized Plan",
      description: "I'll create a customized treatment plan based on your specific needs and goals."
    },
    {
      step: "3",
      title: "Begin Treatment",
      description: "We'll start your hypnotherapy sessions, working at a pace that feels comfortable for you."
    },
    {
      step: "4",
      title: "Track Progress",
      description: "We'll regularly review your progress and adjust the approach as needed to ensure the best results."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Hypnotherapy Services Belfast, Lisburn & Northern Ireland | Clinical Hypnotherapy</title>
        <meta
          name="description"
          content="Professional hypnotherapy services in Belfast, Lisburn & online across Northern Ireland: anxiety treatment, quit smoking, weight loss, phobia removal, trauma recovery, and persistent pain management. Book your free consultation today."
        />
        <meta name="keywords" content="hypnotherapy services Belfast, hypnotherapy Lisburn, hypnotherapy Coleraine, hypnotherapy Northern Ireland, anxiety treatment, quit smoking hypnosis, weight loss hypnotherapy, phobia treatment, trauma therapy, pain management, online hypnotherapy" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/services/" />
        <meta property="og:title" content="Hypnotherapy Services Belfast, Lisburn & Northern Ireland | Clinical Hypnotherapy" />
        <meta property="og:description" content="Professional hypnotherapy services in Belfast, Lisburn & online: anxiety treatment, quit smoking, weight loss, phobia removal, trauma recovery, and persistent pain management." />
        <meta property="og:url" content="https://www.sharonclare-hypnotherapy.com/services/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Hypnotherapy Services Belfast, Lisburn & Northern Ireland | Clinical Hypnotherapy" />
        <meta name="twitter:description" content="Professional hypnotherapy services in Belfast, Lisburn & online: anxiety treatment, quit smoking, weight loss, phobia removal, trauma recovery, and persistent pain management." />
      </Helmet>
      
      <main>
      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-6">
              Hypnotherapy Services
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              Professional <a href="/about" className="text-accent-600 hover:text-accent-700 underline">evidence-based hypnotherapy</a> treatments designed to help you overcome challenges, 
              heal from trauma, and achieve lasting positive change. <a href="/testimonials" className="text-accent-600 hover:text-accent-700 underline">Read our client success stories</a>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Request a 10-Minute Call
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">
              How I Can Help You
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Each service is tailored to your specific needs and designed to create lasting, positive change.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Ready to Start Your Journey?"
        subtitle="Book your free 10-minute consultation today and discover how hypnotherapy can transform your life."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
    </>
  );
};

export default Services;
